/* @flow */
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import type { ContextRouter } from 'react-router-dom';
import _ from 'lodash';

import styles from './styles.module.scss';
import ActionButton from '../../components/ActionButton';
import ActionButtons from '../../components/ActionButtons';
import CoverBanner from '../../components/CoverBanner';
import DataLoader from '../../components/DataLoader';
import Modal from '../../components/Modal';
import ScrollableContent from '../../components/ScrollableContent';
import Sidebar from '../../components/Sidebar';
import Table from '../../components/Table';
import TrackListHeader from '../../components/TrackListHeader';
import TrackListRow from '../../components/TrackListRow';
import WorklistBasicForm from '../../forms/WorklistBasicForm';
import actions from '../../redux/actions';
import { getIsPlaying } from '../../redux/reducers/playerQueue';
import { getSelectedItemIds, getExpandedItemIds } from '../../redux/reducers/smartlist';
import { getTrackSearchParams } from '../../redux/reducers/trackSearch';
import { getSessionUsageId } from '../../redux/reducers/usages';
import { getWorklists, getWorklist1, getWorklist2 } from '../../redux/reducers/worklists';
import protonesUtils from '../../utils/protones';
import stringUtils from '../../utils/string';

import type {
  Fetchable,
  Smartlist,
  SmartlistItem,
  ReduxState,
  SimplifiedWorklist,
  SimplifiedTrack,
  SimplifiedMedia,
  Track,
  Translate,
  SearchParams,
  TrackSearchParams,
} from '../../types';

type Props = ContextRouter & {
  worklists: Fetchable<SimplifiedWorklist[]>,
  worklist1: Fetchable<SimplifiedWorklist>,
  worklist2: Fetchable<SimplifiedWorklist>,
  sessionUsageId: Fetchable<number>,
  isPlaying: boolean,
  currentTrackId: number,
  smartlist: Fetchable<Smartlist>,
  items: Fetchable<SmartlistItem[]>,
  pageScroll: number,
  selectedItems: {
    [itemId: number]: boolean,
  },
  selectedItemIds: number[],
  expandedItems: {
    [itemId: number]: boolean,
  },
  expandedItemIds: number[],
  playTracks: typeof actions.playTracks,
  askQuotation: typeof actions.askQuotation,
  downloadTracks: typeof actions.downloadTracks,
  createCartItemsFromTracks: typeof actions.createCartItemsFromTracks,
  createWorklistItems: typeof actions.createWorklistItems,
  performTrackSearch: typeof actions.performTrackSearch,
  trackSearchParams: TrackSearchParams,
  displayMessage: typeof actions.displayMessage,
  askSmartlist: typeof actions.askSmartlist,
  fetchSmartlist: typeof actions.fetchSmartlist,
  fetchSmartlistItems: typeof actions.fetchSmartlistItems,
  smartlistSetPageScroll: typeof actions.smartlistSetPageScroll,
  smartlistExpandItem: typeof actions.smartlistExpandItem,
  smartlistToggleSelectItems: typeof actions.smartlistToggleSelectItems,
  smartlistToggleExpandSelectedItems: typeof actions.smartlistToggleExpandSelectedItems,
  t: Translate,
};

type State = {
  isCreateWorklistModalVisible: boolean,
  createWorklistWithItemsAction: (any => mixed) | null,
};

const mapStateToProps = (state: ReduxState) => {
  return {
    worklists: getWorklists(state),
    worklist1: getWorklist1(state),
    worklist2: getWorklist2(state),
    sessionUsageId: getSessionUsageId(state),
    currentTrackId: state.playerQueue.currentTrackId,
    isPlaying: getIsPlaying(state),
    trackSearchParams: getTrackSearchParams(state),
    smartlist: state.smartlist.smartlist,
    items: state.smartlist.items,
    pageScroll: state.smartlist.pageScroll,
    selectedItems: state.smartlist.selectedItems,
    selectedItemIds: getSelectedItemIds(state),
    expandedItems: state.smartlist.expandedItems,
    expandedItemIds: getExpandedItemIds(state),
  };
};

class Smartilst extends React.Component<Props, State> {
  state = {
    isCreateWorklistModalVisible: false,
    createWorklistWithItemsAction: null,
  };

  scrollContainer = React.createRef();
  pageScroll: number = 0;

  componentDidMount() {
    const smartlistId = parseInt(this.props.match.params.smartlistId);
    if (!this.props.smartlist.data || this.props.smartlist.data.id !== smartlistId) {
      this.props.fetchSmartlist(smartlistId);
      this.props.fetchSmartlistItems(smartlistId);
    }
    if (this.scrollContainer.current) {
      this.scrollContainer.current.scrollTop = this.props.pageScroll;
    }
  }

  componentWillUnmount() {
    this.props.smartlistSetPageScroll(this.pageScroll);
  }

  _handlePageScroll = (event: any) => {
    const element = event.target;
    const pageScroll = element.scrollTop;
    this.pageScroll = pageScroll;
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _handleRefreshSmartlist = () => {
    const smartlistId = parseInt(this.props.match.params.smartlistId);
    this.props.fetchSmartlist(smartlistId);
    this.props.fetchSmartlistItems(smartlistId);
  };

  _askSmartlist = () => {
    this.props.askSmartlist();
    protonesUtils.goTo(this.props, protonesUtils.routes.ondemand.find());
  };

  _requestCreateWorklistWithItems = (tracks: SimplifiedTrack[], usageId: number) => {
    const { props } = this;
    const { saveWorklist, createWorklistItems, t } = props;
    const createWorklistWithItemsAction = (name: string, description: string) =>
      saveWorklist(
        {
          name: name || t('WORKLIST'),
          description: description || t('NO_DESCRIPTION'),
          usage: protonesUtils.getUsageUpdateParamsFromObject({}),
        },
        async worklist => {
          await createWorklistItems(tracks, worklist, usageId);
          // protonesUtils.goTo(props, protonesUtils.routes.worklists.get(worklist.id));
        }
      );
    this._showCreateWorklistModal(createWorklistWithItemsAction);
  };

  _showCreateWorklistModal = createWorklistWithItemsAction => {
    this.setState({ isCreateWorklistModalVisible: true, createWorklistWithItemsAction });
  };

  _dismissCreateWorklistModal = () => {
    this.setState({ isCreateWorklistModalVisible: false, createWorklistWithItemsAction: null });
  };

  _handleCreateWorklistModalSubmit = worklistParams => {
    const { createWorklistWithItemsAction } = this.state;
    const { name, description } = worklistParams;
    this._dismissCreateWorklistModal();
    createWorklistWithItemsAction(name, description);
  };

  _handleTrackSearch = (params: SearchParams) => {
    const { performTrackSearch, trackSearchParams } = this.props;
    performTrackSearch(_.merge({}, trackSearchParams, params));
    protonesUtils.goTo(this.props, protonesUtils.routes.tracks.find());
  };

  _renderActionButtons = (tracks: Track[]) => {
    const {
      displayMessage,
      createWorklistItems,
      downloadTracks,
      sessionUsageId,
      worklist1,
      worklist2,
      worklists,
      t,
    } = this.props;
    const downloadable = tracks.every(track => track.downloadable === true);
    return (
      <ActionButtons
        download={{
          fetchable: downloadable,
          fetching: false,
          data: () =>
            downloadable
              ? downloadTracks(tracks)
              : displayMessage(t('TRACK_NOT_DOWNLOADABLE'), false),
        }}
        worklist1={{
          fetching: worklist1.fetching || sessionUsageId.fetching,
          error: worklist1.error || sessionUsageId.error,
          data: {
            ...worklist1.data,
            onClick: worklist1.data
              ? () => createWorklistItems(tracks, worklist1.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL1'), true),
          },
        }}
        worklist2={{
          fetching: worklist2.fetching || sessionUsageId.fetching,
          error: worklist2.error || sessionUsageId.error,
          data: {
            ...worklist2.data,
            onClick: worklist2.data
              ? () => createWorklistItems(tracks, worklist2.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL2'), true),
          },
        }}
        worklists={{
          fetching: worklists.fetching || sessionUsageId.fetching,
          error: worklists.error || sessionUsageId.error,
          data: worklists.data && [
            {
              name: t('CREATE'),
            },
            {
              id: 0,
              name: t('NEW_WORKLIST'),
              // $FlowFixMe
              onClick: () => this._requestCreateWorklistWithItems(tracks, sessionUsageId.data),
            },
            {
              name: t('ADD_TO'),
            },
            ...(worklists.data || []).map(worklist => ({
              ...worklist,
              onClick: () =>
                sessionUsageId.data
                  ? () => createWorklistItems(tracks, worklist, sessionUsageId.data)
                  : () => displayMessage(t('SELECT_A_SESSION_USAGE'), true),
            })),
          ],
        }}
      />
    );
  };

  _renderSmartlist = data => {
    const { items } = this.props;
    return (
      <div className={styles.content}>
        <div className={styles.close}>
          <ActionButton iconName={'close'} onClick={this.props.history.goBack} />
        </div>
        {this._renderSmartlistDetails(data)}
        <DataLoader
          loading={items.fetching}
          error={items.error}
          data={items.data}
          render={this._renderItems}
        />
      </div>
    );
  };

  _renderSmartlistDetails = data => {
    return (
      <CoverBanner
        title={data.name}
        subtitle={data.creator}
        description={[data.description, stringUtils.prettifyMoney(data.price)].join('\n')}
        imageSrc={data.coverUrl}
        details={protonesUtils.getMediaDetails(data)}
      />
    );
  };

  _renderItems = data => {
    const { isPlaying, currentTrackId, playTracks } = this.props;
    const items = data;
    const isSelectedAllExpanded = this.props.selectedItemIds.every(id =>
      this.props.expandedItemIds.includes(id)
    );
    const isAllSelected = items.map(t => t.id).every(id => this.props.selectedItemIds.includes(id));
    const isSomeSelected = this.props.selectedItemIds.length > 0;
    const selectedTracks: Track[] = (items || [])
      .filter(item => _.includes(this.props.selectedItemIds, item.id))
      .map(item => item.track);
    return (
      <div className={styles.tracksTable}>
        <Table.Table>
          <TrackListHeader
            showCheckbox={true}
            isSelectable={true}
            isSelected={isAllSelected}
            isPlayerVisible={selectedTracks.length > 0}
            onPlayClick={() => playTracks(selectedTracks)}
            onDetailsExpand={this.props.smartlistToggleExpandSelectedItems}
            isDetailsExpanded={isSelectedAllExpanded}
            showActionButtons={isSomeSelected}
            onCheckboxClick={() => this.props.smartlistToggleSelectItems(items.map(i => i.id))}
            // $FlowFixMe
            actionButtons={this._renderActionButtons(selectedTracks)}
          />
          {items.map((item, index) => {
            const { track } = item;
            const isSelected = !!this.props.selectedItems[item.id];
            const isExpanded = !!this.props.expandedItems[item.id];
            const isLoading = false;
            return (
              <TrackListRow
                key={`${index}-${track.id}`}
                track={track}
                medias={track.medias}
                isSelectable={true}
                isSelected={isSelected}
                isCurrentTrack={currentTrackId === track.id}
                isPlaying={isPlaying}
                isLoading={isLoading}
                isDetailsExpanded={isExpanded}
                onPerformerClick={person =>
                  this._handleTrackSearch({
                    text: person.publicName,
                    flags: { people: true },
                  })
                }
                onAuthorClick={person =>
                  this._handleTrackSearch({
                    text: person.publicName,
                    flags: { people: true },
                  })
                }
                onMediaClick={this._handleMediaClick}
                onCheckboxClick={() => this.props.smartlistToggleSelectItems([item.id])}
                onPlayClick={() => playTracks([track])}
                actionButtons={this._renderActionButtons([track])}
                onDetailsExpand={isExpanded => this.props.smartlistExpandItem(item.id, isExpanded)}
              />
            );
          })}
        </Table.Table>
      </div>
    );
  };

  render() {
    const { t } = this.props;
    const { smartlist, items } = this.props;
    const { isCreateWorklistModalVisible } = this.state;
    const sidebarTitle = _.get(smartlist, 'data.name', undefined) || t('SMARTLIST');
    return (
      <div className={styles.root}>
        <Sidebar
          title={sidebarTitle}
          onRefresh={this._handleRefreshSmartlist}
          loading={smartlist.fetching || items.fetching}
          onAsk={this._askSmartlist}
        />
        <ScrollableContent onScroll={this._handlePageScroll} ref={this.scrollContainer}>
          <DataLoader
            loading={smartlist.fetching}
            error={smartlist.error}
            data={smartlist.data}
            render={this._renderSmartlist}
          />
        </ScrollableContent>
        {isCreateWorklistModalVisible && (
          <Modal
            title={stringUtils.capitalizeString(t('NEW_WORKLIST'))}
            onDismiss={this._dismissCreateWorklistModal}
          >
            <WorklistBasicForm save={this._handleCreateWorklistModalSubmit} />
          </Modal>
        )}
      </div>
    );
  }
}

export default withTranslation()(withRouter(connect(mapStateToProps, actions)(Smartilst)));
